import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Soma500mg() {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [shippingType, setShippingType] = useState<'overseas' | 'us-to-us'>('overseas');

  const product = {
    id: 108,
    name: 'Soma 500mg',
    category: 'Pain Relief',
    images: [
      'https://public.readdy.ai/ai/img_res/1ff181780d0b9ae6a56b5d5b81da75da.jpg'
    ],
    rating: 4.7,
    reviewCount: 634,
    inStock: true,
    description: 'Soma (Carisoprodol) 500mg is a higher-strength muscle relaxant used to treat acute musculoskeletal pain and discomfort. It works by blocking pain sensations between the nerves and the brain, providing effective relief from muscle spasms and injuries.',
    prescriptionRequired: false
  };

  const packagesOverseas = [
    { pills: 100, price: 250 },
    { pills: 170, price: 350 },
    { pills: 250, price: 450 }
  ];

  const packagesUStoUS = [
    { pills: 180, price: 350 },
    { pills: 360, price: 650 },
    { pills: 540, price: 950 }
  ];

  const packages = shippingType === 'overseas' ? packagesOverseas : packagesUStoUS;

  const handleShippingChange = (type: 'overseas' | 'us-to-us') => {
    setShippingType(type);
    setSelectedPackage(0);
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: `${product.id}-${shippingType}-${packages[selectedPackage].pills}`,
      productId: product.id,
      name: product.name,
      image: product.images[0],
      pills: packages[selectedPackage].pills,
      price: packages[selectedPackage].price,
      quantity: quantity,
      shippingType: shippingType
    };

    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItemIndex = existingCart.findIndex(
      (item: any) => item.id === cartItem.id
    );

    if (existingItemIndex > -1) {
      existingCart[existingItemIndex].quantity += quantity;
    } else {
      existingCart.push(cartItem);
    }

    localStorage.setItem('cart', JSON.stringify(existingCart));
    window.dispatchEvent(new Event('cartUpdated'));
    
    // Show success message
    alert('Added to cart successfully!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-2xl text-gray-800"></i>
          </button>
          <h1 className="text-lg font-semibold text-gray-800">Product Details</h1>
          <button
            onClick={() => navigate('/cart')}
            className="w-10 h-10 flex items-center justify-center relative"
          >
            <i className="ri-shopping-cart-line text-2xl text-gray-800"></i>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="pt-16 px-4">
        {/* Image Gallery */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-50">
            <img
              src={product.images[currentImageIndex]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Product Info */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800 mb-1">{product.name}</h2>
              <p className="text-sm text-gray-500">{product.category}</p>
            </div>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <i
                  key={i}
                  className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-yellow-400 text-sm`}
                ></i>
              ))}
            </div>
            <span className="text-sm text-gray-600">
              {product.rating} ({product.reviewCount} reviews)
            </span>
          </div>

          {/* Stock Status */}
          <div className="flex items-center gap-2 mb-4">
            <div className={`w-2 h-2 rounded-full ${product.inStock ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm font-medium ${product.inStock ? 'text-green-600' : 'text-red-600'}`}>
              {product.inStock ? 'In Stock' : 'Out of Stock'}
            </span>
          </div>
        </div>

        {/* Shipping Type Selection */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Shipping Type</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleShippingChange('overseas')}
              className={`p-4 rounded-xl border-2 transition-all ${
                shippingType === 'overseas'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <i className="ri-ship-line text-2xl text-teal-600"></i>
                <p className="font-semibold text-gray-800 text-sm">Overseas</p>
              </div>
            </button>
            <button
              onClick={() => handleShippingChange('us-to-us')}
              className={`p-4 rounded-xl border-2 transition-all ${
                shippingType === 'us-to-us'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <i className="ri-truck-line text-2xl text-teal-600"></i>
                <p className="font-semibold text-gray-800 text-sm">US to US</p>
              </div>
            </button>
          </div>
        </div>

        {/* Package Selection */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Select Package</h3>
          <div className="space-y-2">
            {packages.map((pkg, index) => (
              <button
                key={index}
                onClick={() => setSelectedPackage(index)}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  selectedPackage === index
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <p className="font-semibold text-gray-800">{pkg.pills} Pills</p>
                    <p className="text-sm text-gray-500">Best value for your needs</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-teal-600">${pkg.price}</p>
                    <p className="text-xs text-gray-500">${(pkg.price / pkg.pills).toFixed(2)}/pill</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Quantity Selector */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Quantity</h3>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl"
            >
              <i className="ri-subtract-line text-xl text-gray-800"></i>
            </button>
            <span className="text-2xl font-bold text-gray-800">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl"
            >
              <i className="ri-add-line text-xl text-gray-800"></i>
            </button>
          </div>
        </div>

        {/* Product Description */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Description</h3>
          <p className="text-gray-600 leading-relaxed">{product.description}</p>
        </div>

        {/* SKU */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <p className="text-sm text-gray-500">
            SKU: <span className="font-medium text-gray-700">SOMA-500-{shippingType.toUpperCase()}-{packages[selectedPackage].pills}</span>
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-gray-500">Total Price</p>
            <p className="text-2xl font-bold text-teal-600">
              ${(packages[selectedPackage].price * quantity).toFixed(2)}
            </p>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock}
            className={`px-8 py-3 rounded-xl font-semibold text-white transition-all ${
              product.inStock
                ? 'bg-teal-500 hover:bg-teal-600 active:scale-95'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-shopping-cart-line mr-2"></i>
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}
